import { supabase } from '@/lib/supabase';
import { validateUsername } from '@/lib/username';

// ─────────────────────────────────────────────────────────────
// Création d'un compte travailleur par le marchand.
// Même mécanique que l'inscription : ligne dans `users` + mot de passe
// chiffré (bcrypt), rattachée au commerce du marchand (`idcommerce`).
// Le compte atterrit dans le dashboard (typecompte marchand) et non dans
// l'espace client.
// ─────────────────────────────────────────────────────────────

export interface CreateWorkerInput {
  name: string;
  email: string;
  telephone?: string;
  password: string;
  /** Id du commerce propriétaire (getCommerceId). */
  commerceId: number;
  /** Type du marchand propriétaire (repris pour le travailleur). */
  ownerType: string;
  /** typecompte du marchand propriétaire (repris pour le travailleur). */
  ownerTypecompte: number;
  currency: string;
}

function usernameSeed(email: string, name: string): string {
  const fromEmail = (email.split('@')[0] || '').toLowerCase();
  const raw = fromEmail || name.toLowerCase() || 'agent';
  return raw.replace(/[^a-z0-9._-]/g, '') || 'agent';
}

async function uniqueUsername(seed: string): Promise<string> {
  const { valid, normalized } = validateUsername(seed);
  let base = valid ? normalized : `agent${Date.now().toString().slice(-6)}`;
  let candidate = base;
  for (let i = 0; i < 6; i += 1) {
    const { data } = await supabase
      .from('users')
      .select('id')
      .ilike('user_name', candidate)
      .limit(1);
    if (!data || data.length === 0) return candidate;
    candidate = `${base}${Math.floor(Math.random() * 900 + 100)}`;
  }
  return `${base}${Date.now().toString().slice(-4)}`;
}

/** typecompte sûr : on évite superadmin (0/1), client (6) et développeur (8). */
function safeTypeCompte(ownerTypecompte: number): number {
  const n = Number(ownerTypecompte);
  if (!Number.isFinite(n) || n <= 1 || n === 6 || n === 8) return 2;
  return n;
}

/** Crée le compte (table `users`) d'un nouveau travailleur et renvoie son id. */
export async function createWorkerAccount(input: CreateWorkerInput): Promise<number> {
  const email = input.email.trim().toLowerCase();
  const name = input.name.trim();
  if (!name) throw new Error('Le nom du travailleur est requis.');
  if (!email) throw new Error("L'email est requis.");
  if (!input.password || input.password.length < 6) {
    throw new Error('Le mot de passe doit contenir au moins 6 caractères.');
  }

  const { data: existing } = await supabase.from('users').select('id').eq('email', email);
  if (existing && existing.length > 0) {
    throw new Error('Cet email est déjà utilisé par un autre compte.');
  }

  const username = await uniqueUsername(usernameSeed(email, name));
  const { default: bcrypt } = await import('bcryptjs');
  const hashed = bcrypt.hashSync(input.password, bcrypt.genSaltSync(10));

  const { data, error } = await supabase
    .from('users')
    .insert({
      user_name: username,
      email,
      password: hashed,
      name,
      telephone: input.telephone?.trim() || '',
      type: input.ownerType || 'Commerçant',
      typecompte: safeTypeCompte(input.ownerTypecompte),
      idcommerce: input.commerceId,
      monaie: input.currency,
      langue: 'fr',
      active: 1,
      package: 0,
      payant: 0,
    })
    .select('id')
    .maybeSingle();

  if (error || !data?.id) {
    throw new Error("Impossible de créer le compte du travailleur. Réessayez.");
  }
  return data.id as number;
}